import { useMemo } from "react";
import { Info, Inbox, Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { STATUS_META, formatDate } from "./prosthesis-meta";

type AggregatedIncomingOrder = {
  id: string;
  patientName: string;
  status: keyof typeof STATUS_META;
  deadline: string | Date | null;
  createdAt: string | Date;
  dentistName: string | null;
  organization: { name: string; isPersonal: boolean };
  receivingOrganizationName: string;
};

/** Visão agregada só-leitura da organização Pessoal — pedidos de prótese RECEBIDOS por todas as
 *  organizações (laboratórios) onde o usuário é OWNER. O lado da clínica é a lista de Pedidos do
 *  acesso pessoal (prosthesis.listDeTodasAsOrganizacoes). */
export function ProsthesisIncomingAggregatedView() {
  const query = trpc.prosthesis.listMineIncomingAcrossOrgs.useQuery();
  const orders = useMemo(() => (query.data ?? []) as AggregatedIncomingOrder[], [query.data]);

  return (
    <div className="mx-auto w-full max-w-[1600px] space-y-4 p-4 sm:p-6">
      <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <p className="text-[13px]">
          Visão agregada — só leitura. Mostra os pedidos recebidos por todos os laboratórios onde
          você é dono (OWNER). Pra aceitar/recusar ou mudar status, troque pra organização daquele
          laboratório.
        </p>
      </div>

      {query.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-zinc-200 bg-white py-16 text-center dark:border-border dark:bg-card">
          <Inbox className="mb-2 h-8 w-8 text-zinc-300 dark:text-muted-foreground" />
          <p className="text-sm font-medium text-zinc-900 dark:text-foreground">
            Você ainda não é dono de nenhuma organização
          </p>
          <p className="mt-1 max-w-sm text-xs text-zinc-500 dark:text-muted-foreground">
            Essa visão mostra os pedidos recebidos pelos laboratórios onde você é OWNER. Crie uma
            organização de equipe em Organizações pra começar.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-border dark:bg-card">
          <ul className="divide-y divide-zinc-100 dark:divide-border/60">
            {orders.map((o) => {
              const statusMeta = STATUS_META[o.status];
              const sender = o.organization.isPersonal ? o.dentistName ?? "—" : o.organization.name;
              return (
                <li key={o.id} className="flex items-center gap-3 px-4 py-2.5 text-[13px]">
                  <span
                    className="shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase"
                    style={{ backgroundColor: statusMeta.bg, color: statusMeta.color }}
                  >
                    {statusMeta.label}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-medium text-zinc-900 dark:text-foreground">
                    {o.patientName}
                  </span>
                  <span className="hidden w-40 shrink-0 truncate text-zinc-500 dark:text-muted-foreground sm:inline">
                    {sender}
                  </span>
                  <span className="hidden w-24 shrink-0 text-zinc-500 dark:text-muted-foreground md:inline">
                    {formatDate(o.deadline)}
                  </span>
                  <span className="shrink-0 rounded-full bg-cyan-50 px-2 py-0.5 text-[10px] font-semibold text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-400">
                    {o.receivingOrganizationName}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
